/**
 * One stacked horizontal bar per row — win / partial / lose style splits.
 * Geometry ported from the right pane of block ② in
 * docs/plans/conversations/09-dashboard-mockup.html.
 *
 * - `value` is a SHARE of the row total (the caller divides once; never an average
 *   of rates).
 * - 2px gap in the surface colour between touching segments; only the LAST segment
 *   gets the 4px rounded data end, interior ones stay square.
 * - Inline segment label only when the measured text fits with padding on both
 *   sides, ink picked by `inkOn`. A segment too narrow is carried by the legend +
 *   tooltip — never clipped.
 * - Legend always present (>= 2 series by construction).
 */

import { Box } from '@mui/material';
import { VIZ, fmtInt, fmtPct, hBarPath, inkOn, measureText, useMeasuredWidth } from './tokens';
import { EmptyNote, VizLegend } from './ChartCard';
import { useVizTooltip } from './useVizTooltip';

export interface StackedBarsRow {
  label: string;
  n?: number;
  parts: { key: string; label: string; value: number; color: string }[];
}

export interface StackedBarsProps {
  rows: StackedBarsRow[];
  format?: (v: number) => string;
  emptyNote?: string;
}

const BAR_H = 22;
const GAP_X = 2; // surface-colour gap between segments
const ROW_H = 46;
const PAD = 6; // minimum padding on both sides of an inline label

export default function StackedBars({
  rows,
  format = fmtPct,
  emptyNote = 'Aucune donnée sur cette sélection.',
}: StackedBarsProps) {
  const [ref, width] = useMeasuredWidth<HTMLDivElement>(640);
  const tip = useVizTooltip();

  if (!rows.length || !rows[0].parts.length) return <EmptyNote>{emptyNote}</EmptyNote>;

  const labelW = Math.min(220, Math.max(80, ...rows.map((r) => measureText(r.label, 12) + 14)));
  const m = { l: labelW, r: 8, t: 6 };
  const W = Math.max(320, width);
  const H = rows.length * ROW_H + 10;
  const pw = Math.max(40, W - m.l - m.r);

  return (
    <Box ref={ref} sx={{ width: '100%' }}>
      <svg
        viewBox={`0 0 ${W} ${H}`}
        width="100%"
        style={{ display: 'block', overflow: 'visible' }}
        role="img"
        aria-label={`Répartition ${rows[0].parts.map((p) => p.label).join(' / ')} par ligne`}
      >
        {rows.map((row, i) => {
          const y = m.t + i * ROW_H + 4;
          const total = row.parts.reduce((s, p) => s + p.value, 0) || 1;
          let x = m.l;
          return (
            <g key={row.label}>
              <text x={m.l - 10} y={y + BAR_H - 6} textAnchor="end" fill={VIZ.ink2} fontSize={12}>
                {row.label}
              </text>
              {row.n != null && (
                <text
                  x={m.l - 10}
                  y={y + BAR_H + 9}
                  textAnchor="end"
                  fill={VIZ.muted}
                  fontSize={10.5}
                  style={{ fontVariantNumeric: 'tabular-nums' }}
                >
                  {fmtInt(row.n)}
                </text>
              )}
              {row.parts.map((p, j) => {
                const last = j === row.parts.length - 1;
                const w = Math.max(0, (p.value / total) * pw - (last ? 0 : GAP_X));
                const sx = x;
                x += w + GAP_X;
                const text = format(p.value);
                const fits = measureText(text, 11.5, 600) + 2 * PAD <= w;
                return (
                  <g key={p.key}>
                    <path
                      d={last ? hBarPath(sx, y, w, BAR_H, 4) : `M${sx},${y}h${w}v${BAR_H}h${-w}z`}
                      fill={p.color}
                      style={{ cursor: 'pointer' }}
                      onPointerMove={(ev) =>
                        tip.show(ev, [`${row.label} · ${p.label}`, format(p.value)])
                      }
                      onPointerLeave={tip.hide}
                    />
                    {fits && (
                      <text
                        x={sx + w / 2}
                        y={y + BAR_H / 2 + 4}
                        textAnchor="middle"
                        fill={inkOn(p.color)}
                        fontSize={11.5}
                        fontWeight={600}
                        style={{ pointerEvents: 'none', fontVariantNumeric: 'tabular-nums' }}
                      >
                        {text}
                      </text>
                    )}
                  </g>
                );
              })}
            </g>
          );
        })}
      </svg>
      <VizLegend items={rows[0].parts.map((p) => ({ label: p.label, color: p.color }))} />
      {tip.node}
    </Box>
  );
}
